/**
 * Conversion d'un montant en toutes lettres (français), pour les reçus de caisse.
 *
 * Règles appliquées : « et un » (vingt et un… soixante et onze), soixante-dix / quatre-vingt(s),
 * accord de « cent » et « quatre-vingt » au pluriel seulement en fin de nombre,
 * « mille » invariable, « million(s) » et « milliard(s) » accordés.
 *
 * @example montantEnLettres(1_250_080) // "un million deux cent cinquante mille quatre-vingts"
 */

const UNITES = [
  'zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
  'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize',
  'dix-sept', 'dix-huit', 'dix-neuf',
];
const DIZAINES = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];

/** 0 à 99 */
function deuxChiffres(n: number, finDeNombre: boolean): string {
  if (n < 20) return UNITES[n];
  if (n < 70) {
    const d = Math.floor(n / 10);
    const u = n % 10;
    if (u === 0) return DIZAINES[d];
    return `${DIZAINES[d]}${u === 1 ? ' et un' : `-${UNITES[u]}`}`;
  }
  if (n < 80) {
    // soixante-dix à soixante-dix-neuf
    const reste = n - 60;
    return reste === 11 ? 'soixante et onze' : `soixante-${UNITES[reste]}`;
  }
  // quatre-vingts à quatre-vingt-dix-neuf
  const reste = n - 80;
  if (reste === 0) return finDeNombre ? 'quatre-vingts' : 'quatre-vingt';
  return `quatre-vingt-${UNITES[reste]}`;
}

/** 0 à 999 (renvoie '' pour 0) */
function troisChiffres(n: number, finDeNombre: boolean): string {
  if (n === 0) return '';
  const c = Math.floor(n / 100);
  const reste = n % 100;
  let texte = '';
  if (c > 0) {
    texte = c === 1 ? 'cent' : `${UNITES[c]} cent`;
    if (c > 1 && reste === 0 && finDeNombre) texte += 's';
  }
  if (reste > 0) {
    texte += (texte ? ' ' : '') + deuxChiffres(reste, finDeNombre);
  }
  return texte;
}

export function nombreEnLettres(valeur: number): string {
  const n = Math.floor(Math.abs(valeur));
  if (!Number.isFinite(n)) return '';
  if (n === 0) return 'zéro';

  const milliards = Math.floor(n / 1_000_000_000);
  const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
  const milliers = Math.floor((n % 1_000_000) / 1000);
  const unites = n % 1000;

  const parties: string[] = [];
  if (milliards > 0) {
    parties.push(`${troisChiffres(milliards, true)} milliard${milliards > 1 ? 's' : ''}`);
  }
  if (millions > 0) {
    parties.push(`${troisChiffres(millions, true)} million${millions > 1 ? 's' : ''}`);
  }
  if (milliers > 0) {
    // « mille » invariable, et jamais « un mille »
    parties.push(milliers === 1 ? 'mille' : `${troisChiffres(milliers, false)} mille`);
  }
  if (unites > 0) {
    parties.push(troisChiffres(unites, true));
  }
  return parties.join(' ');
}

/** « Cinquante mille francs CFA » */
export function montantEnLettres(montant: number, devise = 'francs CFA'): string {
  const texte = nombreEnLettres(montant);
  return `${texte.charAt(0).toUpperCase()}${texte.slice(1)} ${devise}`;
}
